import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, Coins, Trophy, Flame, Gamepad2, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Leaderboard } from "./Leaderboard";

const BANNER = "https://static.prod-images.emergentagent.com/jobs/44692715-2b06-4f33-8719-88e36ed25b28/images/55d7635900d9f4969dadbad9e2cb0f2ddc6e1285ef76867f35ec4091a1ed3403.jpeg";
const HIDE = new Set(["jixxeo-playzone", "jixxeo-balloon-pop"]);

function MyStats({ me }) {
  const item = (icon, label, value, id) => (
    <div className="pz-stat text-left" data-testid={id}>
      <div className="pz-chip-label flex items-center gap-1">{icon}{label}</div>
      <div className="text-xl font-black">{value}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pz-my-stats">
      {item(<Trophy className="w-3 h-3" />, "Best", me.best.toLocaleString(), "pz-stat-best")}
      {item(<Flame className="w-3 h-3" />, "This week", me.week_best.toLocaleString(), "pz-stat-week")}
      {item(<Star className="w-3 h-3" />, "Rank", me.rank_all ? `#${me.rank_all}` : "—", "pz-stat-rank")}
      {item(<Coins className="w-3 h-3" />, "Coins today", `${me.coins_today}/${me.daily_cap}`, "pz-stat-coins")}
    </div>
  );
}

function GameTile({ g, i }) {
  const internal = g.launch_url?.startsWith("/");
  const to = internal ? g.launch_url : `/app/${g.slug}`;
  return (
    <Link to={to} className="group pz-tile pz-rise" style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }} data-testid={`pz-game-${g.slug}`}>
      <div className="aspect-square rounded-2xl overflow-hidden bg-white/5 ring-1 ring-white/10">
        {g.icon_url ? <img src={g.icon_url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          : <Gamepad2 className="w-10 h-10 m-auto mt-[35%] text-white/30" />}
      </div>
      <div className="mt-2 text-sm font-bold truncate">{g.name.replace(/^Jixxeo\s+/, "")}</div>
      <div className="text-[11px] text-white/50 truncate">{g.tagline}</div>
    </Link>
  );
}

export default function PlayZone() {
  const { user } = useAuth();
  const [games, setGames] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => { document.title = "Jixxeo PlayZone"; api.get("/playzone/games").then((r) => setGames(r.data.games)).catch(() => setGames([])); }, []);
  useEffect(() => { if (user) api.get("/playzone/balloon/me").then((r) => setMe(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { if (window.location.hash === "#leaderboard") setTimeout(() => document.getElementById("leaderboard")?.scrollIntoView({ behavior: "smooth" }), 400); }, []);
  const list = (games || []).filter((g) => !HIDE.has(g.slug));
  return (
    <div className="min-h-screen pz-root text-white" data-testid="playzone-home">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#07060f]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <Link to="/" className="pz-icon-btn !w-9 !h-9" aria-label="Back to Jixxeo Apps" data-testid="pz-back-home"><ArrowLeft className="w-4 h-4" /></Link>
          <span className="font-black tracking-tight text-lg" style={{ fontFamily: "Cabinet Grotesk, Outfit" }}>Play<span className="text-[#FF2E88]">Zone</span></span>
          <span className="flex-1" />
          {user ? <span className="flex items-center gap-1.5 text-sm font-bold" data-testid="pz-wallet"><Coins className="w-4 h-4 text-[#FFB800]" />{(user.coins || 0).toLocaleString()}</span>
            : <Link to="/login?next=/playzone" className="text-xs font-bold px-4 py-2 rounded-full bg-white text-black" data-testid="pz-signin">Sign in</Link>}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-10">
        <section className="relative rounded-[28px] overflow-hidden min-h-[320px] md:min-h-[400px] flex items-end pz-rise" data-testid="pz-hero">
          <img src={BANNER} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07060f] via-[#07060f]/40 to-transparent" />
          <div className="relative p-6 md:p-10 max-w-xl">
            <div className="pz-kicker">New · 3D arcade</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]" style={{ fontFamily: "Cabinet Grotesk, Outfit" }}>Balloon Pop</h1>
            <p className="text-white/75 mt-3 text-sm md:text-base">60 seconds. Glossy 3D balloons. Chain combos, grab the gold, dodge the bombs — and earn coins into your Jixxeo wallet.</p>
            <Link to="/playzone/balloon-pop" className="pz-btn mt-5 inline-flex" data-testid="pz-play-balloon"><Play className="w-5 h-5" fill="currentColor" />Play now</Link>
          </div>
        </section>
        {user && me && <MyStats me={me} />}
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 order-2 lg:order-1">
            <h2 className="text-base md:text-lg font-black mb-4 flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-[#00E5FF]" />All Jixxeo games</h2>
            {games === null ? <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square rounded-2xl bg-white/5 animate-pulse" />)}</div>
              : !list.length ? <p className="text-sm text-white/50" data-testid="pz-no-games">More games are on the way.</p>
              : <div className="grid grid-cols-3 sm:grid-cols-4 gap-4" data-testid="pz-games-grid">{list.map((g, i) => <GameTile key={g.slug} g={g} i={i} />)}</div>}
          </div>
          <div className="lg:col-span-2 order-1 lg:order-2"><Leaderboard meId={user?.id} /></div>
        </div>
      </main>
    </div>
  );
}
